import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@core/api';

/**
 * Chat API using RTK Query
 *
 * Provides chat endpoints with automatic caching,
 * request deduplication, and loading state management.
 *
 * Uses centralized baseQuery for consistent error handling.
 */
export const chatApi = createApi({
  reducerPath: 'chatApi',
  baseQuery,
  tagTypes: ['ChatHistory', 'ChatSessions'],
  // Cache configuration for chat data
  keepUnusedDataFor: 300, // 5 minutes cache for chat history
  refetchOnMountOrArgChange: 60, // Refetch if data is 1 minute old
  endpoints: (builder) => ({
    getChatHistory: builder.query({
      query: (sessionId) => ({
        url: '/api/v1/agent-chat/',
        params: sessionId ? { session_id: sessionId } : {},
      }),
      providesTags: ['ChatHistory'],
      transformResponse: (response) => {
        // Map snake_case quick_replies to camelCase for consistency
        const history = (response.history || []).map((msg) => ({
          ...msg,
          quickReplies: msg.quick_replies || [],
        }));
        return {
          sessionId: response.session_id,
          isActive: response.is_active ?? true, // Default to true for backwards compatibility
          history,
        };
      },
    }),
    getChatSessions: builder.query({
      query: () => '/api/v1/agent-chat/sessions/',
      providesTags: ['ChatSessions'],
      transformResponse: (response) => {
        return response.sessions || [];
      },
    }),
    sendMessage: builder.mutation({
      query: ({ message, sessionId, context = {}, force_new_session = false }) => ({
        url: '/api/v1/agent-chat/',
        method: 'POST',
        body: {
          message,
          session_id: sessionId,
          context,
          force_new_session,
        },
      }),
      invalidatesTags: ['ChatHistory', 'ChatSessions'],
      transformResponse: (response) => {
        return {
          sessionId: response.session_id,
          content: response.content,
          citations: response.citations || [],
          needsAttention: response.needs_attention || false,
          quickReplies: response.quick_replies || [],
        };
      },
      transformErrorResponse: (response) => {
        if (response.data) {
          const errorMessage = Object.entries(response.data)
            .map(([field, messages]) => {
              if (Array.isArray(messages)) {
                return `${field}: ${messages.join(', ')}`;
              }
              return `${field}: ${messages}`;
            })
            .join('\n');
          return { message: errorMessage };
        }
        return { message: 'Failed to send message' };
      },
    }),
    endSession: builder.mutation({
      query: ({ sessionId }) => ({
        url: '/api/v1/agent-chat/end-session/',
        method: 'POST',
        body: {
          session_id: sessionId,
        },
      }),
      invalidatesTags: ['ChatHistory', 'ChatSessions'],
    }),
  }),
});

export const {
  useGetChatHistoryQuery,
  useGetChatSessionsQuery,
  useSendMessageMutation,
  useEndSessionMutation,
} = chatApi;
